import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { View, KeyboardAvoidingView, Platform, useWindowDimensions, TouchableOpacity, ScrollView, Keyboard } from 'react-native'
import { ThemeConsumer } from '@rneui/themed'
import { useFormik } from 'formik'
import * as Yup from "yup"
import Popover from 'react-native-popover-view'

import { config } from '../../../shared/environment'

import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox'

import { Code } from '../../customers/addEditCustomerModule'
import { ProductDetails, UpdateProductPayloadProps } from './productModule'

import { getOnePosProductDetailsRedux, updateProductDetails } from '../../../redux/slices/productSlice'
import { getMeasurement } from '../../../redux/slices/commonSlice'
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../../redux/hooks'

import { ZenDropdown, ZenIcon, ZenInput, ZenLoader, ZenText, ZenToast } from 'zen-core-elements'
import { CustomModal } from '../../../shared/commonComponents/customModal'
import Toast from 'react-native-toast-message'
import { ZenToastConfig } from 'zen-core-elements/src/elements/ZenToastMessage/zenToast'
import { shallowEqual } from 'react-redux'

export type QuickEditSetStateType = { isVisible: boolean, productId: number | null }
//SelectCustomerModal props
type SelectCustomerModalProps = {
    setModalVisible: Dispatch<SetStateAction<QuickEditSetStateType>>,
    modalVisible: QuickEditSetStateType
    categoryOptions: Code[]
}
// Variable which is used to hold multiTextArray values
let multiTextArray: string[] = [];
export const QuickProductEdit: FC<SelectCustomerModalProps> = ({ setModalVisible, modalVisible, categoryOptions }) => {
    const { isWrite: isProductWrite } = useAppSelector(getScreenPermission("ProductStack"), shallowEqual)
    //State variable to use width measurement from useWindowDimensions hook
    const { width, height } = useWindowDimensions()
    //State variable to use useAppDispatch hook
    const dispatch = useAppDispatch()
    const [productDetail, setProductDetail] = useState<ProductDetails>()
    //Varaible to handle current dropdown open state
    const [categoryOpen, setCategoryOpen] = useState(false)
    const [weightUnitOpen, setWeightUnitOpen] = useState(false)
    const [measurementData, setMeasurementData] = useState<Code[]>([])
    const [loading, setLoading] = useState(true)
    const [leavePopover, setLeavePopover] = useState(false)
    const [updateLoader, setUpdateLoader] = useState(false)

    // Variable which is used to hold multi category value
    const [multiCategory, setMultiCategory] = useState<string[]>([])

    useEffect(() => {
        if (modalVisible?.productId) {
            setLoading(true)
            getProductDetails()
        }
    }, [modalVisible?.productId])

    const getProductDetails = async () => {
        await dispatch(getOnePosProductDetailsRedux({ id: modalVisible?.productId!, storeId: config?.storeId })).unwrap().then((async (response) => {
            setProductDetail(response?.productDetails)
            let tempArray: string[] = [];

            response?.productDetails?.productCategories?.find((value) => {
                categoryOptions?.map((category) => {
                    if (category.value == value?.categoryId) {
                        multiTextArray?.push(category?.label);
                        tempArray = multiTextArray?.filter(
                            (item, i, newArray) =>
                                newArray.indexOf(item) === i,
                        );
                    }
                })
            });
            setMultiCategory([...tempArray]?.sort((a, b) => a?.localeCompare(b)));
            if (measurementData.length === 0) {
                await getWeightUnit()
            } else {
                setLoading(false)
            }
        })).catch((error) => {
            setModalVisible({ isVisible: false, productId: null })
            ZenToast({
                type: "error",
                message: "Failed to get the product details, Please try again after some time!!"
            })
            console.log("error", error)
        })
    }

    const getWeightUnit = async () => {
        await dispatch(getMeasurement(config?.storeId)).unwrap().then((response) => {
            let data: Code[] = []
            response?.unitData?.map((value) => {
                if (value?.code == "MASS") {
                    value?.units?.map((subValue) => {
                        data.push({ label: subValue?.abbrevation, value: subValue?.id })
                    })
                }
            })
            setMeasurementData(data)
            setLoading(false)
        }).catch((error) => {
            setModalVisible({ isVisible: false, productId: null })
            ZenToast({
                type: "error",
                message: "Failed to get the weight count details, Please try again after some time!!"
            })
            console.log("Measurement error", error)
        })
    }
    const validationSchema = Yup.object()?.shape({
        name: Yup.string().required("Product name is required"),
        sellingPrice: Yup.string().required("Selling price is required")
            .matches(/^\d+(\.\d{1,2})?$/, { message: "Please enter a valid selling price" }),
        mrp: Yup.string()
            .matches(/^\d+(\.\d{1,2})?$/, { message: "Please enter a valid MRP" })
            .when('sellingPrice', (sellingPrice, schema) => {
                return schema.test(
                    'is-greater-or-equal',
                    Number(sellingPrice[0]) ? 'MRP should be greater than or equal to the selling price' : !sellingPrice[0] ? "Please enter the selling price" : "Please enter a valid selling price",
                    (mrp) => {
                        return !mrp ? true : Number(sellingPrice[0]) ? parseFloat(mrp) >= parseFloat(sellingPrice[0]) : false
                    }
                );
            }),
        wholeSalePrice: Yup.string()
            .matches(/^\d+(\.\d{1,2})?$/, { message: "Please enter a valid wholeSalePrice" })
            .when('sellingPrice', (sellingPrice, schema) => {
                return schema.test(
                    'is-less-or-equal',
                    Number(sellingPrice[0]) ? 'Wholesale price should be less than or equal to the selling price' : !sellingPrice[0] ? "Please enter the selling price" : "Please enter a valid selling price",
                    (wholeSalePrice) => {
                        return !wholeSalePrice ? true : Number(sellingPrice[0]) ? parseFloat(wholeSalePrice) <= parseFloat(sellingPrice[0]) : false
                    }
                );
            }),
        // Was unvalidated: "abc" was sent to updateProductDetails as the cost price
        actualPrice: Yup.string()
            .matches(/^\d+(\.\d{1,2})?$/, { message: "Please enter a valid actual price" }),
    })

    const formik = useFormik({
        initialValues: {
            name: productDetail?.name || "",
            categories: productDetail?.productCategories?.map((item) => item?.categoryId) || [],
            weightUnit: productDetail?.weightUnitId || "",
            sellingPrice: productDetail?.variantCombinationDetails[0]?.price || "",
            mrp: productDetail?.variantCombinationDetails[0]?.costComparePrice || "",
            actualPrice: productDetail?.variantCombinationDetails[0]?.cost || "",
            wholeSalePrice: productDetail?.variantCombinationDetails[0]?.wholeSalePrice || ""
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: () => {
            Keyboard?.dismiss()
            setUpdateLoader(true)
            updateProductDetailsApi()
        }
    })

    const updateProductDetailsApi = async () => {
        let payload: UpdateProductPayloadProps = {
            productName: formik?.values?.name,
            relatedCategoryId: formik?.values?.categories,
            weightUnitId: Number(formik?.values?.weightUnit) || null,
            sellingPrice: formik?.values?.sellingPrice.toString(),
            mrp: formik?.values?.mrp.toString() || null,
            actualPrice: formik?.values?.actualPrice.toString() || null,
            wholesale: formik?.values?.wholeSalePrice || null,
            isVariant: productDetail?.isVariants!!,
            isDigitalProduct: false,
            pluginPresent: true
        }
        await dispatch(updateProductDetails({ storeId: config?.storeId, productId: modalVisible?.productId!!, payload })).unwrap().then((response) => {
            if (response) {
                setModalVisible({ isVisible: false, productId: null })
                ZenToast({
                    type: "success",
                    message: "Product Updated successfully "
                })
            }
        }).catch((error) => {
            ZenToast({
                type: "error",
                message: "Failed to update the product, Please try again after some time!!"
            })
            console.log("Update product details error", error)
        }).finally(() => {
            setUpdateLoader(false)
        })
    }


    const handleBackPress = () => {
        if (formik?.dirty) {
            setLeavePopover(true)
        } else {
            setModalVisible({ isVisible: false, productId: null })
        }
    }
    const confirmation = (value: { id: number }) => {
        setLeavePopover(false)
        if (value.id == 2) {
            setModalVisible({ isVisible: false, productId: null })
        }
    }

    return (
        <ThemeConsumer>
            {({ theme }) => (
                <CustomModal visible={modalVisible?.isVisible} transparent>
                    <KeyboardAvoidingView behavior="padding" style={theme?.quickProductEditStyle.keyboardAvoidingView}>
                        <View style={theme?.modifierSetStyle?.modalOuterView}>
                            <KeyboardAvoidingView onStartShouldSetResponder={() => {
                                setCategoryOpen(false)
                                setWeightUnitOpen(false)
                                return false
                            }} behavior={Platform.OS === "ios" ? "padding" : undefined} enabled>
                                <View style={[theme?.modifierSetStyle?.innerView, { width: width > 900 ? "60%" : width > 600 ? "80%" : "90%", overflow: "visible" }]}>
                                    <View style={[theme?.modifierSetStyle?.modelHeader]}>
                                        <ZenText content={"Quick Edit"} bold={true} size={width > 600 ? theme?.mediumFontSize : theme?.smallerFontSize} color={theme?.dark} />
                                        <TouchableOpacity onPress={handleBackPress} >
                                            <ZenIcon name='close-circle' type='material-community' size={width > 600 ? 24 : 22} color={theme?.iconColor} />
                                        </TouchableOpacity>
                                    </View>
                                    {loading ?
                                        <View style={{ height: height / 2 }}>
                                            <ZenLoader viewStyle={theme?.quickProductEditStyle.loaderView} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
                                        </View>
                                        :
                                        <View>
                                            <View style={[theme?.quickProductEditStyle.scrollView, { maxHeight: height / 2 }]}>
                                                <ScrollView showsVerticalScrollIndicator={false} scrollEnabled>
                                                    <View>
                                                        <ZenInput
                                                            value={formik?.values?.name}
                                                            autoCapitalize={"none"}
                                                            disabled={!isProductWrite}
                                                            onChangeText={(text: string) => formik?.setFieldValue("name", text)}
                                                            label={"Product Name *"}
                                                            labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                            onBlur={formik?.handleBlur("name")}
                                                            onEndEditing={() => { formik?.setFieldValue("name", formik?.values?.name?.trim()) }}
                                                            inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                            errorMessage={formik?.errors?.name && !formik?.isValid && formik?.touched?.name ? formik?.errors?.name?.toString() : ""}
                                                            errorStyle={[theme?.quickProductEditStyle.errorStyle, { paddingVertical: (formik?.errors?.name && !formik?.isValid && formik?.touched?.name) ? 10 : 0 }]}

                                                            type="outlined"
                                                            containerStyle={theme?.quickProductEditStyle.inputContainer}
                                                            inputContainerStyle={theme?.quickProductEditStyle.inputInnerContainer}
                                                        />
                                                    </View>
                                                    <View style={theme?.quickProductEditStyle.dropdownContainer}>
                                                        <ZenDropdown
                                                            title="Category"
                                                            disabled={!isProductWrite}
                                                            titleStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                            items={categoryOptions}
                                                            open={categoryOpen}
                                                            setOpen={setCategoryOpen}
                                                            setValue={() => { }}
                                                            value={formik?.values?.categories}
                                                            multipleText={multiCategory ? multiCategory?.length > 3 ? multiCategory?.slice(0, 3).concat('....').toString() : multiCategory?.toString() : ''}
                                                            onChangeValue={(item) => {
                                                                let tempArray: string[];
                                                                multiTextArray = []
                                                                categoryOptions?.find((items) => {
                                                                    item?.find((value) => {
                                                                        if (items.value === value) {
                                                                            multiTextArray.push(items?.label);
                                                                            tempArray = multiTextArray?.filter(
                                                                                (item, i, newArray) =>
                                                                                    newArray.indexOf(item) === i,
                                                                            );
                                                                        }
                                                                        setMultiCategory(tempArray);
                                                                    });
                                                                });
                                                            }}
                                                            onSelectItem={(data) => {
                                                                formik.setFieldValue("categories", data.map((items: any) => items.value))
                                                            }}
                                                            searchable={true}
                                                            multiple={true}
                                                            autoScroll={true}
                                                            placeholder={"Select a categories"}
                                                            listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
                                                            labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
                                                            placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
                                                        />
                                                    </View>
                                                    <View style={theme?.quickProductEditStyle.weightUnitContainer}>
                                                        <ZenDropdown
                                                            title="Weight Unit"
                                                            disabled={!isProductWrite}
                                                            titleStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                            items={measurementData}
                                                            open={weightUnitOpen}
                                                            setOpen={setWeightUnitOpen}
                                                            dropDownContainerStyle={{ position: productDetail?.isVariants ? "relative" : "absolute", top: productDetail?.isVariants ? 0 : "100%" }}
                                                            setValue={() => { }}
                                                            value={formik?.values?.weightUnit}
                                                            onSelectItem={(values) => {
                                                                formik?.setFieldValue("weightUnit", values?.value)
                                                            }}
                                                            searchable={true}
                                                            multiple={false}
                                                            autoScroll={true}
                                                            placeholder={"Select a weightUnit"}
                                                            listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
                                                            labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
                                                            placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
                                                        />
                                                    </View>
                                                    {!productDetail?.isVariants && <View>
                                                        <View>
                                                            <ZenInput
                                                                value={formik?.values?.sellingPrice.toString()}
                                                                disabled={!isProductWrite}
                                                                autoCapitalize={"none"}
                                                                onChangeText={(text: string) => formik?.setFieldValue("sellingPrice", text)}
                                                                label={"Selling Price *"}
                                                                labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                                onBlur={formik?.handleBlur("sellingPrice")}
                                                                keyboardType='number-pad'
                                                                onEndEditing={() => { formik?.setFieldValue("sellingPrice", formik?.values?.sellingPrice?.toString().trim()) }}
                                                                inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                                errorMessage={formik?.errors?.sellingPrice && !formik?.isValid && formik?.touched?.sellingPrice ? formik?.errors?.sellingPrice?.toString() : ""}
                                                                errorStyle={[theme?.quickProductEditStyle.errorStyle, { paddingVertical: (formik?.errors?.sellingPrice && !formik?.isValid && formik?.touched?.sellingPrice) ? 10 : 0 }]}

                                                                type="outlined"
                                                                containerStyle={theme?.quickProductEditStyle.inputContainer}
                                                                inputContainerStyle={theme?.quickProductEditStyle.inputInnerContainer}
                                                            />
                                                        </View>
                                                        <View>
                                                            <ZenInput
                                                                value={formik?.values?.mrp.toString()}
                                                                disabled={!isProductWrite}
                                                                autoCapitalize={"none"}
                                                                onChangeText={(text: string) => formik?.setFieldValue("mrp", text)}
                                                                label={"MRP"}
                                                                labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                                keyboardType='numbers-and-punctuation'
                                                                onEndEditing={() => { formik?.setFieldValue("mrp", formik?.values?.mrp?.toString().trim()) }}
                                                                inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                                errorMessage={formik?.errors?.mrp && !formik?.isValid ? formik?.errors?.mrp?.toString() : ""}
                                                                errorStyle={[theme?.quickProductEditStyle.errorStyle, { paddingVertical: (formik?.errors?.mrp && !formik?.isValid) ? 10 : 0 }]}

                                                                type="outlined"
                                                                containerStyle={theme?.quickProductEditStyle.inputContainer}
                                                                inputContainerStyle={theme?.quickProductEditStyle.inputInnerContainer}
                                                            />
                                                        </View>
                                                        <View>
                                                            <ZenInput
                                                                value={formik?.values?.actualPrice.toString()}
                                                                disabled={!isProductWrite}
                                                                autoCapitalize={"none"}
                                                                onChangeText={(text: string) => formik?.setFieldValue("actualPrice", text)}
                                                                label={"Actual Price"}
                                                                labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                                keyboardType='numbers-and-punctuation'
                                                                onEndEditing={() => { formik?.setFieldValue("actualPrice", formik?.values?.actualPrice?.toString().trim()) }}
                                                                inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                                errorMessage={formik?.errors?.actualPrice && !formik?.isValid ? formik?.errors?.actualPrice?.toString() : ""}
                                                                errorStyle={[theme?.quickProductEditStyle.errorStyle, { paddingVertical: (formik?.errors?.actualPrice && !formik?.isValid) ? 10 : 0 }]}

                                                                type="outlined"
                                                                containerStyle={theme?.quickProductEditStyle.inputContainer}
                                                                inputContainerStyle={theme?.quickProductEditStyle.inputInnerContainer}
                                                            />
                                                        </View>
                                                        <View>
                                                            <ZenInput
                                                                value={formik?.values?.wholeSalePrice.toString()}
                                                                autoCapitalize={"none"}
                                                                disabled={!isProductWrite}
                                                                onChangeText={(text: string) => formik?.setFieldValue("wholeSalePrice", text)}
                                                                label={"Wholesale Price"}
                                                                labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                                keyboardType='numbers-and-punctuation'
                                                                onEndEditing={() => { formik?.setFieldValue("wholeSalePrice", formik?.values?.wholeSalePrice?.toString().trim()) }}
                                                                inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                                errorMessage={formik?.errors?.wholeSalePrice && !formik?.isValid ? formik?.errors?.wholeSalePrice?.toString() : ""}
                                                                errorStyle={[theme?.quickProductEditStyle.errorStyle, { paddingVertical: (formik?.errors?.wholeSalePrice && !formik?.isValid) ? 10 : 0 }]}

                                                                type="outlined"
                                                                containerStyle={theme?.quickProductEditStyle.inputContainer}
                                                                inputContainerStyle={theme?.quickProductEditStyle.inputInnerContainer}
                                                            />
                                                        </View>
                                                    </View>}
                                                </ScrollView>
                                            </View>
                                            <View style={theme?.quickProductEditStyle.actionView}>
                                                <TouchableOpacity
                                                    style={{
                                                        borderRadius: 7,
                                                        borderWidth: 1,
                                                        borderColor: theme.primary,
                                                        paddingVertical: 9,
                                                        paddingHorizontal: 14,
                                                        justifyContent: 'center',
                                                        alignItems: 'center'
                                                    }}
                                                    onPress={handleBackPress}
                                                    accessibilityLabel={"Quick edit cancel button"}
                                                >
                                                    <ZenText content="Cancel" bold size={theme?.smallFontSize} color={theme?.primary} />
                                                </TouchableOpacity>

                                                {isProductWrite && <TouchableOpacity
                                                    style={{
                                                        borderRadius: 7,
                                                        backgroundColor: theme.primary,
                                                        paddingVertical: 9,
                                                        paddingHorizontal: 14,
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                    }}
                                                    onPress={() => { formik?.handleSubmit() }}
                                                    accessibilityLabel={"Quick edit Update button"}
                                                >
                                                    <ZenText content="Update" bold color={theme.light} size={theme?.smallFontSize} />
                                                </TouchableOpacity>}
                                            </View>
                                        </View>}
                                    {/* Popover for delete confirmation */}
                                    <Popover isVisible={leavePopover} popoverStyle={{ width: width > 600 ? 500 : 350, borderRadius: 10 }}>
                                        <CommonDialogBox type="EDIT" replaceValue="" confirmation={confirmation} />
                                    </Popover>
                                </View>
                            </KeyboardAvoidingView>
                            <Toast config={ZenToastConfig} position='bottom' bottomOffset={30} />
                        </View>
                    </KeyboardAvoidingView>
                </CustomModal>
            )}
        </ThemeConsumer>
    )
}